import { Card, CardContent, CardHeader, CardTitle } from '@/_shared/components/ui/card';
import { Skeleton } from '@/_shared/components/ui/skeleton';
import { CHART_COLORS } from '@/_shared/lib/charts/colors';

interface FunnelStep {
  label: string;
  count: number;
}

interface FunnelChartProps {
  title: string;
  steps: FunnelStep[];
  loading?: boolean;
}

function formatCount(count: number): string {
  return count.toLocaleString('en-US');
}

function getBarWidthPercent(count: number, maxCount: number): number {
  if (maxCount === 0) return 0;
  return (count / maxCount) * 100;
}

export function FunnelChart({ title, steps, loading }: FunnelChartProps): React.ReactElement {
  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-medium text-muted-foreground">{title}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-3/4" />
            <Skeleton className="h-8 w-1/2" />
          </div>
        </CardContent>
      </Card>
    );
  }

  const firstCount = steps[0]?.count ?? 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm font-medium text-muted-foreground">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-1">
          {steps.map((step, index) => {
            const color = CHART_COLORS[index % CHART_COLORS.length];
            const widthPercent = getBarWidthPercent(step.count, firstCount);
            const overallPercent = firstCount > 0 ? ((step.count / firstCount) * 100).toFixed(0) : '0';

            const prevStep = index > 0 ? steps[index - 1] : undefined;
            const conversionRate =
              prevStep && prevStep.count > 0 ? ((step.count / prevStep.count) * 100).toFixed(1) : undefined;

            return (
              <div key={step.label}>
                {conversionRate !== undefined && (
                  <div className="flex items-center gap-2 py-1 pl-4">
                    <span className="text-xs text-muted-foreground">
                      {'\u2193'} {conversionRate}%
                    </span>
                  </div>
                )}
                <div className="flex items-center gap-3">
                  <span className="w-28 shrink-0 truncate text-xs text-muted-foreground">{step.label}</span>
                  <div className="relative h-6 flex-1 rounded bg-card">
                    <div
                      className="h-full rounded"
                      style={{
                        width: `${widthPercent}%`,
                        backgroundColor: color,
                        minWidth: step.count > 0 ? '4px' : '0px',
                        transition: 'width 0.4s ease-out',
                      }}
                    />
                  </div>
                  <span className="w-20 shrink-0 text-right text-xs text-foreground tabular-nums">
                    {formatCount(step.count)}
                  </span>
                  <span className="w-12 shrink-0 text-right text-xs text-muted-foreground tabular-nums">
                    {overallPercent}%
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
